if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.hoverMql = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.pointer = { x: null, y: null };
      this.pointerMoveScheduled = false;
      this.lastPointerActivationTs = 0;
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        const button = mediaToSwitch.querySelector('button');
        if (!button) return;

        const setActive = () => {
          // Avoid redundant work while moving across thumbnails
          if (button.getAttribute('aria-current') === 'true') return;
          this.setActiveMedia(mediaToSwitch.dataset.target, false);
        };

        button.addEventListener('click', setActive);
        // Desktop: switch media on hover
        button.addEventListener('mouseenter', () => {
          if (!this.hoverMql.matches) return;
          setActive();
        });
      });

      // When using the wheel to scroll thumbnails, the mouse may stay still so
      // mouseenter won't fire. Track the pointer and activate the thumbnail
      // currently under the cursor after wheel-driven movement.
      this.elements.thumbnails.addEventListener('mousemove', (event) => {
        if (!this.hoverMql.matches) return;
        this.pointer.x = event.clientX;
        this.pointer.y = event.clientY;
        if (this.pointerMoveScheduled) return;
        this.pointerMoveScheduled = true;
        requestAnimationFrame(() => {
          this.pointerMoveScheduled = false;
          this.activateThumbnailFromPointer();
        });
      });

      // Capture phase ensures this still runs even if an inner wheel handler stops propagation.
      this.elements.thumbnails.addEventListener('wheel', (event) => {
        if (!this.hoverMql.matches) return;
        if (this.pointer.x == null || this.pointer.y == null) {
          this.pointer.x = event.clientX;
          this.pointer.y = event.clientY;
        }

        // Wait until after any slider logic runs and DOM is updated.
        requestAnimationFrame(() => {
          this.activateThumbnailFromPointer();
        });
      }, { capture: true, passive: true });

      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    activateThumbnailFromPointer() {
      const now = performance.now();
      if (now - this.lastPointerActivationTs < 80) return;

      const x = this.pointer.x;
      const y = this.pointer.y;
      if (x == null || y == null) return;

      const element = document.elementFromPoint(x, y);
      if (!element) return;

      const button = element.closest && element.closest('button.thumbnail');
      if (!button || !this.elements.thumbnails.contains(button)) return;
      if (button.getAttribute('aria-current') === 'true') return;

      const item = button.closest && button.closest('[data-target]');
      if (!item || !item.dataset || !item.dataset.target) return;
      this.lastPointerActivationTs = now;
      this.setActiveMedia(item.dataset.target, false);
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail && activeThumbnail.parentElement) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail ? activeThumbnail.dataset.mediaPosition : '');
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (typeof this.elements.thumbnails.isSlideVisible === 'function' && this.elements.thumbnails.slider) {
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        return;
      }

      thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}

// Mobile/touch UX: ensure tapping the media itself opens the product media modal.
// Dawn's modal-opener binds only to its internal button; on mobile users often tap
// the image/icon area. This forwards such taps to the button click.
document.addEventListener('click', (event) => {
  const opener = event.target && event.target.closest && event.target.closest('modal-opener.product__modal-opener');
  if (!opener) return;

  // Scope to product media galleries only.
  if (!opener.closest('media-gallery')) return;

  // If the click is already on the button, let the built-in handler run.
  if (event.target.closest('button')) return;

  const button = opener.querySelector('button.product__media-toggle');
  if (!button) return;
  button.click();
});
